// src/api/encounterApi.ts

import { createAsyncThunk } from "@reduxjs/toolkit"
import { criticalApi, serverApi, handleApiResponse, handlePageResponse, getAuthHeaders, queApi } from "@core/api/core"
import type {
  EncounterBaseSerializer,
  EncounterResponse,
  EncounterCreatedSerializer,
  EncounterExternalDataSerializer,
  EncounterExternalNoteSerializer,
  EncounterAutoInsuranceGuidanceResponse,
  EncounterMajorTreatmentGuidanceResponse,
} from "@/types/encounter"
import { CommonResponse, CommonTResponse } from "@/types/common"
import { RequestEncounterSerializer } from "@/types/encounter"
import { EncounterUninsuredListSerializer } from "@/types/encounterItem"
import { PaginationType } from "@/types/common"

// ──────────────────────────────────────────────────────────────────────────────
// 처방 클릭시 약재 구성 조회
// ──────────────────────────────────────────────────────────────────────────────

export const getEncounter = async (params: RequestEncounterSerializer): Promise<EncounterBaseSerializer> => {
  const requestUrl = `/encounter/`
  const response = await criticalApi.get(requestUrl, { params })

  return handleApiResponse<EncounterBaseSerializer>(response)
}

export const getEncounterByPatient = async (patientUuid: string, encounterDate: string): Promise<EncounterBaseSerializer> => {
  const requestUrl = `/encounter/`
  const params = { patientUuid, encounterDate }
  const response = await criticalApi.get<EncounterResponse>(requestUrl, { params })
  return handleApiResponse<EncounterBaseSerializer>(response)
}

export const getEncounterByDateVisit = async (encounterW: EncounterBaseSerializer): Promise<EncounterBaseSerializer> => {
  const requestUrl = `/encounter/`
  const params = {
    visitUuid: encounterW.visitUuid || "",
    insuranceUuid: encounterW.insuranceUuid,
    encounterDate: encounterW.encounterDate,
  }
  const response = await criticalApi.get<EncounterResponse>(requestUrl, { params })
  return handleApiResponse<EncounterBaseSerializer>(response)
}

export const getEncounterByDate = async (patientUuid: string, encounterDate: string): Promise<EncounterBaseSerializer> => {
  const requestUrl = `/encounter/`
  const params = { patientUuid, encounterDate }
  const response = await criticalApi.get<EncounterResponse>(requestUrl, { params })
  return handleApiResponse<EncounterBaseSerializer>(response)
}

export const getEncounterExternalNote = async (encounterUuid: string): Promise<EncounterExternalNoteSerializer> => {
  const requestUrl = `/encounter/${encounterUuid}/external-note/`
  const response = await criticalApi.get(requestUrl)
  return handleApiResponse<EncounterExternalNoteSerializer>(response)
}

export const updateEncounterExternalNote = async (encounterUuid: string, externalNote: string): Promise<EncounterExternalNoteSerializer> => {
  const requestUrl = `/encounter/${encounterUuid}/external-note/`
  const response = await criticalApi.patch(requestUrl, {
    externalNote,
  })
  return handleApiResponse<EncounterExternalNoteSerializer>(response)
}

export const getEncounterExternalData = async (encounterUuid: string): Promise<EncounterExternalDataSerializer> => {
  const requestUrl = `/encounter/${encounterUuid}/external-data/`
  const response = await criticalApi.get(requestUrl)
  return handleApiResponse<EncounterExternalDataSerializer>(response)
}

export const updateEncounterExternalData = async (
  encounterUuid: string,
  externalData: Record<string, unknown>
): Promise<EncounterExternalDataSerializer> => {
  const requestUrl = `/encounter/${encounterUuid}/external-data/`
  const response = await criticalApi.patch(requestUrl, {
    externalData,
  })
  return handleApiResponse<EncounterExternalDataSerializer>(response)
}

export const analyzeEncounterVoiceCharting = async (
  encounterUuid: string,
  voiceChartingText: string
): Promise<{ content: string; analyzed: boolean }> => {
  const requestUrl = `/encounter/${encounterUuid}/analyze-voice-charting/`
  const response = await criticalApi.post(requestUrl, { voiceChartingText })
  return handleApiResponse<{ content: string; analyzed: boolean }>(response)
}

export const getEncounterAutoInsuranceGuidance = async (
  insuranceUuid: string,
  accidentDate: string,
  excludeEncounterDate?: string | null
): Promise<EncounterAutoInsuranceGuidanceResponse | null> => {
  const requestUrl = `/encounter/auto_insurance_guidance/`
  const params = { insuranceUuid, accidentDate, excludeEncounterDate }
  const response = await criticalApi.get(requestUrl, { params })
  return handleApiResponse<EncounterAutoInsuranceGuidanceResponse | null>(response)
}

export const getEncounterMajorTreatmentGuidance = async (encounterUuid: string): Promise<EncounterMajorTreatmentGuidanceResponse | null> => {
  const requestUrl = `/encounter/major_treatment_guidance/`
  const params = { encounterUuid }
  const response = await criticalApi.get(requestUrl, { params })
  return handleApiResponse<EncounterMajorTreatmentGuidanceResponse | null>(response)
}

export const createEncounter = async (encounterW: EncounterBaseSerializer): Promise<EncounterBaseSerializer> => {
  const requestUrl = `/encounter/`
  const payload = {
    patientUuid: encounterW.patientUuid,
    insuranceUuid: encounterW?.insuranceUuid,
    encounterDate: encounterW.encounterDate,
    memberUuid: encounterW.memberUuid,
  }
  const response = await criticalApi.post(requestUrl, payload)
  // const response = await serverApi.post(requestUrl, payload)
  return handleApiResponse<EncounterBaseSerializer>(response)
}

/**
 * null을 서버에 명시적으로 전달해야 하는 필드 목록.
 * null이 "값 없음(삭제)"을 의미하는 필드만 포함.
 * 그 외 필드는 실수로 null이 들어와도 payload에서 제외하여 기존 서버 값을 보호.
 */
const NULLABLE_ENCOUNTER_FIELDS: ReadonlySet<string> = new Set(["pregnancyDate", "expectedDeliveryDate", "encounterEtcData"])

export const updateEncounter = async (
  encounterW: EncounterBaseSerializer,
  options?: { idempotencyKey?: string; expectedUpdatedDt?: string | null }
): Promise<EncounterBaseSerializer> => {
  const requestUrl = `/encounter/${encounterW.encounterUuid}/`
  const payload: any = {
    encounterUuid: encounterW.encounterUuid,
  }

  // encounterW의 모든 필드를 동적으로 처리
  Object.entries(encounterW).forEach(([key, value]) => {
    // encounterUuid는 이미 포함했으므로 제외
    if (key === "encounterUuid") return
    // null 허용 필드: null도 서버에 전달 (값 초기화)
    // 그 외 필드: null은 payload에서 제외하여 서버 기존값 보호
    if (value !== undefined && (value !== null || NULLABLE_ENCOUNTER_FIELDS.has(key))) {
      payload[key] = value
    }
  })
  if (options?.expectedUpdatedDt) {
    payload.expectedUpdatedDt = options.expectedUpdatedDt
  }

  // 페이로드 로깅 추가

  const response = await criticalApi.patch<CommonTResponse<EncounterBaseSerializer>>(requestUrl, payload, undefined, options?.idempotencyKey)
  return handleApiResponse<EncounterBaseSerializer>(response)
}

export const updateEncounterPin = async (encounterUuid: string, encounterIsPinned: boolean): Promise<EncounterBaseSerializer> => {
  const response = await criticalApi.patch<CommonTResponse<EncounterBaseSerializer>>(`/encounter/${encounterUuid}/pin/`, {
    encounterIsPinned,
  })
  return handleApiResponse<EncounterBaseSerializer>(response)
}

export const reassignEncounterMember = async (encounterUuid: string, memberUuid: string): Promise<EncounterBaseSerializer> => {
  const requestUrl = `/encounter/${encounterUuid}/reassign-member/`
  const response = await criticalApi.patch<CommonTResponse<EncounterBaseSerializer>>(requestUrl, {
    memberUuid,
  })
  return handleApiResponse<EncounterBaseSerializer>(response)
}

export const changeEncounterInsurance = async (encounterUuid: string, insuranceUuid: string): Promise<EncounterBaseSerializer> => {
  const requestUrl = `/encounter/${encounterUuid}/change-insurance/`
  const response = await criticalApi.patch<CommonTResponse<EncounterBaseSerializer>>(requestUrl, {
    insuranceUuid,
  })
  return handleApiResponse<EncounterBaseSerializer>(response)
}

export const fetchUpdateEncounter = createAsyncThunk<
  EncounterBaseSerializer, // 성공 시 반환할 타입
  { encounterW: EncounterBaseSerializer }, // 인자로 받을 객체 타입
  { rejectValue: string }
>("encounter/fetchUpdateEncounter", async ({ encounterW }, { rejectWithValue }) => {
  try {
    const requestUrl = `/encounter/${encounterW.encounterUuid}/`

    // 업데이트할 데이터만 포함하도록 필터링
    const updateData: any = {
      encounterUuid: encounterW.encounterUuid,
    }

    // encounterW의 모든 필드를 동적으로 처리
    Object.entries(encounterW).forEach(([key, value]) => {
      // encounterUuid는 이미 포함했으므로 제외
      if (key === "encounterUuid") return
      // null 허용 필드: null도 서버에 전달 (값 초기화)
      // 그 외 필드: null은 payload에서 제외하여 서버 기존값 보호
      if (value !== undefined && (value !== null || NULLABLE_ENCOUNTER_FIELDS.has(key))) {
        updateData[key] = value
      }
    })

    const response = await criticalApi.patch<EncounterResponse>(requestUrl, updateData)
    return handleApiResponse<EncounterBaseSerializer>(response)
  } catch (err: any) {
    // err.response.data.message
    return rejectWithValue(err?.response?.data?.message || err?.message || "차트 수정을 실패했습니다.")
  }
})

/**
 * deleteEncounter 함수는 지정된 encounterUuid의 진료 기록을 삭제합니다.
 *
 * @param encounterUuid 삭제할 진료 기록의 UUID
 * @returns Promise<boolean> - 삭제 성공 여부
 */
export const deleteEncounter = async (encounterUuid: string): Promise<boolean> => {
  const requestUrl = `/encounter/${encounterUuid}/`

  const response = await criticalApi.delete<CommonResponse>(requestUrl)
  return handleApiResponse<boolean>(response)
}

// 비급여 코드 사용 진료기록을 위한 확장 타입
export interface EncounterWithPatientInfo extends EncounterBaseSerializer {
  patientName?: string
  patientBirth?: string
  patientPhone?: string
}

// ──────────────────────────────────────────────────────────────────────────────
// 비급여 코드별 진료기록 조회 (DUMMY)
// ──────────────────────────────────────────────────────────────────────────────
export const getEncountersByUninsuredCode = async (uninsuredItemCode: string): Promise<EncounterWithPatientInfo[]> => {
  // 더미 데이터 생성
  await new Promise(resolve => setTimeout(resolve, 100)) // 로딩 시뮬레이션

  const dummyData: EncounterWithPatientInfo[] = [
    {
      encounterUuid: "enc-001",
      patientUuid: "patient-001",
      patientName: "김철수",
      patientBirth: "1980-01-15",
      patientPhone: "010-1234-5678",
      encounterDate: "2025-09-28",
      plainNote: "목과 어깨 통증으로 내원. 침 치료 진행.",
      claimSubmittedDt: "2025-09-29T10:00:00Z",
      memberUuid: "doctor-001",
      insuranceUuid: "ins-001",
    },
    {
      encounterUuid: "enc-002",
      patientUuid: "patient-002",
      patientName: "박영희",
      patientBirth: "1975-03-22",
      patientPhone: "010-9876-5432",
      encounterDate: "2025-09-27",
      plainNote: "요통으로 내원하여 한약 처방.",
      claimSubmittedDt: undefined, // 미청구
      memberUuid: "doctor-002",
      insuranceUuid: "ins-002",
    },
    {
      encounterUuid: "enc-003",
      patientUuid: "patient-003",
      patientName: "이민수",
      patientBirth: "1990-07-08",
      patientPhone: "010-5555-1234",
      encounterDate: "2025-09-26",
      plainNote: "소화불량 및 위장 장애로 내원.",
      claimSubmittedDt: "2025-09-27T14:30:00Z",
      memberUuid: "doctor-001",
      insuranceUuid: "ins-003",
    },
    {
      encounterUuid: "enc-004",
      patientUuid: "patient-004",
      patientName: "최영수",
      patientBirth: "1965-11-30",
      patientPhone: "010-7777-8888",
      encounterDate: "2025-09-25",
      plainNote: "무릎 관절염으로 물리치료 받음.",
      claimSubmittedDt: undefined, // 미청구
      memberUuid: "doctor-003",
      insuranceUuid: "ins-004",
    },
    {
      encounterUuid: "enc-005",
      patientUuid: "patient-005",
      patientName: "정미영",
      patientBirth: "1988-05-12",
      patientPhone: "010-2222-3333",
      encounterDate: "2025-09-24",
      plainNote: "스트레스성 두통으로 침 치료 및 상담.",
      claimSubmittedDt: "2025-09-25T09:15:00Z",
      memberUuid: "doctor-002",
      insuranceUuid: "ins-005",
    },
  ]

  return dummyData
}

// ──────────────────────────────────────────────────────────────────────────────
// 비급여 항목별 진료 내역 조회 (페이지네이션)
// ──────────────────────────────────────────────────────────────────────────────

/**
 * 비급여 항목명으로 진료 내역 조회 (페이지네이션)
 * @param encounterItemName - 비급여 항목명 (예: "한약", "약침" 등, 쉼표 구분 가능). 보험약 이력에서는 빈 문자열 허용
 * @param page - 페이지 번호 (기본값: 1)
 * @param search - 환자명, 처방명 또는 항목명 검색 (OR 조건)
 * @returns 페이지네이션 정보와 비급여 항목 리스트
 */
export const getEncounterItemListByUninsuredName = async (
  encounterItemName: string,
  page: number = 1,
  search?: string,
  startDate?: string,
  endDate?: string,
  isInsured?: boolean,
  ordering?: string, // encounter_date | -encounter_date | expiration_date | -expiration_date
  itemType?: string, // all | herbal | jabo | nhis | insurance_drug
  memberUuid?: string,
  categoryName?: string,
  processStatus?: string,
  herbalPrescriptions?: boolean
): Promise<{ pagination: PaginationType; results: EncounterUninsuredListSerializer[] }> => {
  const requestUrl = `/encounter/uninsured/`
  const params: any = { encounter_item_name: encounterItemName, page }
  if (herbalPrescriptions) params.herbal_prescriptions = true

  // 검색 키워드 (환자명, 처방명 또는 항목명)
  if (search && search.trim()) {
    params.search = search.trim()
  }

  if (startDate) {
    params.start_date = startDate
  }

  if (endDate) {
    params.end_date = endDate
  }

  if (typeof isInsured === "boolean") {
    params.is_insured = isInsured
  }

  if (ordering) {
    params.ordering = ordering
  }

  if (itemType && itemType !== "all") {
    params.item_type = itemType
  }

  if (memberUuid) {
    params.memberUuid = memberUuid
  }

  if (categoryName) {
    params.category_name = categoryName
  }

  if (processStatus && processStatus !== "all") {
    params.process_status = processStatus
  }

  const response = await serverApi.get(requestUrl, { params })
  return handlePageResponse<EncounterUninsuredListSerializer>(response)
}

// ──────────────────────────────────────────────────────────────────────────────
// 진료완료 업데이트 API
// ──────────────────────────────────────────────────────────────────────────────
export const completeEncounterPrescription = async (encounterUuid: string): Promise<boolean> => {
  const requestUrl = `/encounter/${encounterUuid}/complete-prescription/`
  const response = await serverApi.patch<CommonTResponse<boolean>>(requestUrl, {})
  // console.log("completeEncounterPrescription response:", response)
  return handleApiResponse<boolean>(response)
}

// deprecated 호환용 fallback.
// 구버전 서버에서 complete-prescription endpoint를 지원하지 않을 때만 사용한다.
// 구버전 서버 지원 종료 시 completeEncounterPrescription의 fallback 호출과 함께 삭제한다.
export const updateEncounterAsCompleted = async (encounterUuid: string, isPrescriptionCompleted: boolean): Promise<boolean> => {
  const requestUrl = `/encounter/${encounterUuid}/`
  const payload: any = {
    encounterUuid: encounterUuid,
    isPrescriptionCompleted: isPrescriptionCompleted,
  }

  const response = await serverApi.patch<CommonTResponse<EncounterBaseSerializer>>(requestUrl, payload)
  // console.log("updateEncounterAsCompleted response:", response)
  return handleApiResponse<boolean>(response)
}
